import React, { useEffect, useRef, useState } from "react";

const CustomCursor = () => {
  const dotRef = useRef(null);
  const loopRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    // 1. Hide on mobile/touch interfaces or reduced-motion systems
    const isMobile = window.matchMedia("(pointer: coarse)").matches;
    const isSmallScreen = window.innerWidth < 1024;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (isMobile || isSmallScreen || prefersReducedMotion) {
      setIsDisabled(true);
      return;
    }

    const dot = dotRef.current;
    const loop = loopRef.current;
    if (!dot || !loop) return;

    let mouseX = 0;
    let mouseY = 0;
    let loopX = 0;
    let loopY = 0;

    const onMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      setIsVisible(true);

      // Direct coordinates for the inner dot
      dot.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0) translate(-50%, -50%)`;
    };

    const animateLoop = () => {
      // Elastic easing calculation
      loopX += (mouseX - loopX) * 0.12;
      loopY += (mouseY - loopY) * 0.12;

      loop.style.transform = `translate3d(${loopX}px, ${loopY}px, 0) translate(-50%, -50%)`;

      requestAnimationFrame(animateLoop);
    };

    const loopAnimId = requestAnimationFrame(animateLoop);

    // 2. Track hover states for interactive tags
    const onMouseEnterInteractive = () => setIsHovering(true);
    const onMouseLeaveInteractive = () => setIsHovering(false);

    const updateInteractiveListeners = () => {
      const interactiveElements = document.querySelectorAll(
        "a, button, select, input, textarea, [role='button'], .cursor-pointer"
      );

      interactiveElements.forEach((el) => {
        el.addEventListener("mouseenter", onMouseEnterInteractive);
        el.addEventListener("mouseleave", onMouseLeaveInteractive);
      });
    };

    window.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseleave", () => setIsVisible(false));
    document.addEventListener("mouseenter", () => setIsVisible(true));

    // Initial listener setup
    updateInteractiveListeners();

    // Setup MutationObserver to watch DOM updates (e.g. page switches/routes)
    const observer = new MutationObserver(() => {
      updateInteractiveListeners();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      cancelAnimationFrame(loopAnimId);
      observer.disconnect();

      const interactiveElements = document.querySelectorAll(
        "a, button, select, input, textarea, [role='button'], .cursor-pointer"
      );
      interactiveElements.forEach((el) => {
        el.removeEventListener("mouseenter", onMouseEnterInteractive);
        el.removeEventListener("mouseleave", onMouseLeaveInteractive);
      });
    };
  }, []);

  if (isDisabled || !isVisible) return null;

  return (
    <>
      <div
        ref={dotRef}
        className="custom-cursor-dot pointer-events-none fixed top-0 left-0 rounded-full z-[9999]"
      />
      <div
        ref={loopRef}
        className={`custom-cursor-loop pointer-events-none fixed top-0 left-0 rounded-full z-[9998] ${
          isHovering ? "custom-cursor-hovering" : ""
        }`}
      />
    </>
  );
};

export default CustomCursor;
